/**
 * Lightweight in-memory logger that powers the on-page diagnostic panel.
 *
 * Design goals:
 *  - SIGNAL over noise: most polling / repeated state should NOT emit a log.
 *  - State-change detection: env snapshots are de-duplicated via a fingerprint
 *    so opening + 5s of polling produces ~5 lines, not ~500.
 *  - Auto-installs error/warn capture so any module can `import` and just call
 *    uistep / uiwarn / uierror. Plain console.log is NOT captured anymore
 *    because most of it is unrelated app chatter.
 */

const MAX_ENTRIES = 1500

let entries = []
const subscribers = new Set()
let installed = false
const bootMs = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()

let lastEnvFingerprint = ''

function nowStamp() {
  const d = new Date()
  const pad = (n, w) => String(n).padStart(w, '0')
  return (
    pad(d.getHours(), 2) +
    ':' +
    pad(d.getMinutes(), 2) +
    ':' +
    pad(d.getSeconds(), 2) +
    '.' +
    pad(d.getMilliseconds(), 3)
  )
}

function elapsedMs() {
  const now = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()
  return Math.round(now - bootMs)
}

function safeStringify(v) {
  if (v === null) return 'null'
  if (v === undefined) return 'undefined'
  const t = typeof v
  if (t === 'string') return v
  if (t === 'number' || t === 'boolean' || t === 'bigint') return String(v)
  if (t === 'function') return '[Function ' + (v.name || 'anonymous') + ']'
  if (v instanceof Error) {
    const code = v.code !== undefined ? ' code=' + v.code : ''
    return 'Error' + code + ': ' + (v.message || '') + (v.stack ? '\n' + v.stack : '')
  }
  try {
    return JSON.stringify(
      v,
      (key, val) => {
        if (typeof val === 'function') return '[Function]'
        if (val instanceof Error) {
          return { name: val.name, message: val.message, code: val.code }
        }
        if (val && typeof val === 'object' && val.nodeType) return '[DOM]'
        return val
      }
    )
  } catch (e) {
    try {
      return String(v)
    } catch (e2) {
      return '[unserializable]'
    }
  }
}

function pushEntry(level, parts) {
  const text = parts.map((p) => safeStringify(p)).join(' ')
  const entry = { t: nowStamp(), e: elapsedMs(), level, text }
  entries.push(entry)
  if (entries.length > MAX_ENTRIES) {
    entries.splice(0, entries.length - MAX_ENTRIES)
  }
  subscribers.forEach((fn) => {
    try {
      fn(entry, false)
    } catch (e) {
      /* no-op */
    }
  })
  return entry
}

export function uilog(...args) {
  pushEntry('log', args)
}
export function uiinfo(...args) {
  pushEntry('info', args)
}
export function uiwarn(...args) {
  pushEntry('warn', args)
}
export function uierror(...args) {
  pushEntry('err', args)
}
export function uistep(tag, ...args) {
  pushEntry('step', ['[' + tag + ']', ...args])
}

export function getEntries() {
  return entries.slice()
}

export function clearEntries() {
  entries = []
  lastEnvFingerprint = ''
  subscribers.forEach((fn) => {
    try {
      fn(null, true)
    } catch (e) {
      /* no-op */
    }
  })
}

export function subscribe(fn) {
  subscribers.add(fn)
  return () => subscribers.delete(fn)
}

export function formatForCopy() {
  const head = '=== UBS DApp Diagnostic Log (' + new Date().toISOString() + ') ===\n' +
    'entries=' + entries.length + ' bootMs=' + Math.round(bootMs) + '\n'
  return head + entries.map((e) => '[' + e.t + '] +' + e.e + 'ms [' + e.level + '] ' + e.text).join('\n')
}

/**
 * Build a compact single-line snapshot of wallet-related globals.
 * Returns { line, fingerprint } so callers can de-dup unchanged states.
 */
function buildEnvSnapshot() {
  if (typeof window === 'undefined') {
    return { line: 'env: (no window)', fingerprint: 'no-window' }
  }
  const w = window
  const eth = w.ethereum
  const tw = w.trustwallet || w.trustWallet
  const handlers = w.webkit && w.webkit.messageHandlers ? Object.keys(w.webkit.messageHandlers) : []
  const probe = w.__ubsEarlyWalletProbe
  const announces = probe && Array.isArray(probe.announces) ? probe.announces.length : 0
  const requests = probe && typeof probe.requestsDispatched === 'number' ? probe.requestsDispatched : 0
  const walletGlobals = Object.keys(w).filter((k) =>
    /wallet|trust|web3|ethereum|tron|coinbase|okx|phantom|rabby|metamask|binance/i.test(k)
  )

  let ethFlags = '-'
  if (eth) {
    const flags = []
    if (eth.isMetaMask) flags.push('mm')
    if (eth.isTrust) flags.push('trust')
    if (eth.isTrustWallet) flags.push('tw')
    if (eth.isCoinbaseWallet) flags.push('cb')
    if (eth.isOkxWallet) flags.push('okx')
    const providersLen = Array.isArray(eth.providers) ? eth.providers.length : 0
    ethFlags = (flags.length ? flags.join('+') : 'unknown') + (providersLen ? '/p' + providersLen : '')
  }

  let twNs = '-'
  if (tw && typeof tw === 'object') {
    const keys = Object.keys(tw)
    twNs = '[' + keys.join(',') + ']' + (typeof tw.request === 'function' ? ' itself' : '') + (tw.ethereum && typeof tw.ethereum.request === 'function' ? ' eth' : '')
  }

  const parts = [
    'eth=' + (eth ? ethFlags : 'NO'),
    'twNs=' + twNs,
    'tw3p=' + (typeof w.TrustWeb3Provider !== 'undefined' ? 'YES' : 'NO'),
    'okx=' + (typeof w.okxwallet !== 'undefined' ? 'YES' : 'NO'),
    'tron=' + (w.tronWeb ? (w.tronWeb.ready ? 'ready' : 'present') : 'NO'),
    'wkH=' + handlers.length + (handlers.length ? '[' + handlers.join(',') + ']' : ''),
    'probe.announces=' + announces,
    'probe.requests=' + requests,
    'globals=' + walletGlobals.length + (walletGlobals.length ? '[' + walletGlobals.join(',') + ']' : '')
  ]

  // Fingerprint excludes the request count (which only goes up over time)
  const fpParts = parts.filter((p) => !p.startsWith('probe.requests='))
  return {
    line: parts.join(' '),
    fingerprint: fpParts.join('|')
  }
}

/**
 * Compact env dump. By default it only emits a log if the state CHANGED since
 * the last call. Pass `{ force: true }` to always emit (useful for boot/timeout).
 */
export function dumpEnvironment(label, opts) {
  const force = !!(opts && opts.force)
  const snap = buildEnvSnapshot()
  if (!force && snap.fingerprint === lastEnvFingerprint) {
    return
  }
  lastEnvFingerprint = snap.fingerprint
  pushEntry('env', ['[' + (label || 'env') + ']', snap.line])
}

/** Verbose dump — only used on-demand from the UI button. */
export function dumpEnvironmentVerbose(label) {
  if (typeof window === 'undefined') {
    pushEntry('env', ['(no window)'])
    return
  }
  const w = window
  const eth = w.ethereum
  const tw = w.trustwallet || w.trustWallet
  const probe = w.__ubsEarlyWalletProbe
  const env = {
    label: label || 'env-verbose',
    href: location.href,
    readyState: document.readyState,
    ua: navigator.userAgent,
    viewport: window.innerWidth + 'x' + window.innerHeight,
    eth: eth ? {
      isMetaMask: !!eth.isMetaMask,
      isTrust: !!eth.isTrust,
      isTrustWallet: !!eth.isTrustWallet,
      isCoinbaseWallet: !!eth.isCoinbaseWallet,
      isOkxWallet: !!eth.isOkxWallet,
      providersLen: Array.isArray(eth.providers) ? eth.providers.length : 0,
      hasRequest: typeof eth.request === 'function',
      chainId: eth.chainId || null
    } : null,
    twNs: tw && typeof tw === 'object' ? {
      keys: Object.keys(tw),
      ethHasRequest: !!(tw.ethereum && typeof tw.ethereum.request === 'function')
    } : null,
    hasTrustWeb3Provider: typeof w.TrustWeb3Provider !== 'undefined',
    webkitHandlers: w.webkit && w.webkit.messageHandlers ? Object.keys(w.webkit.messageHandlers) : null,
    walletGlobals: Object.keys(w).filter((k) =>
      /wallet|trust|web3|ethereum|tron|coinbase|okx|phantom|rabby|metamask|binance/i.test(k)
    ),
    earlyProbe: probe ? {
      announcesCount: (probe.announces || []).length,
      announcesSummary: (probe.announces || []).map((a) => ({
        elapsedMs: a.elapsedMs,
        rdns: a.info && a.info.rdns,
        name: a.info && a.info.name
      })),
      requestsDispatched: probe.requestsDispatched,
      snapshotsCount: (probe.snapshots || []).length,
      lastSnapshot: probe.snapshots ? probe.snapshots[probe.snapshots.length - 1] : null
    } : null
  }
  pushEntry('env', [JSON.stringify(env, null, 2)])
}

export function installGlobalHooks() {
  if (installed || typeof window === 'undefined') return
  installed = true

  // Only hook warn/error — plain console.log is mostly app chatter that
  // drowns out the diagnostic signal we care about.
  const _warn = console.warn
  const _error = console.error

  console.warn = function () {
    const args = Array.prototype.slice.call(arguments)
    try {
      pushEntry('warn', args)
    } catch (e) {
      /* no-op */
    }
    try {
      _warn.apply(console, args)
    } catch (e) {
      /* no-op */
    }
  }
  console.error = function () {
    const args = Array.prototype.slice.call(arguments)
    try {
      pushEntry('err', args)
    } catch (e) {
      /* no-op */
    }
    try {
      _error.apply(console, args)
    } catch (e) {
      /* no-op */
    }
  }

  window.addEventListener('error', function (event) {
    try {
      pushEntry('uncaught', [
        'window.error:',
        event.message,
        'at',
        (event.filename || '') + ':' + (event.lineno || 0) + ':' + (event.colno || 0)
      ])
    } catch (e) {
      /* no-op */
    }
  })
  window.addEventListener('unhandledrejection', function (event) {
    try {
      const r = event.reason
      pushEntry('unhandled', [
        'unhandledrejection:',
        r && r.message ? r.message : safeStringify(r)
      ])
    } catch (e) {
      /* no-op */
    }
  })

  pushEntry('boot', ['uiLogger installed at ' + nowStamp() + ' bootMs=' + Math.round(bootMs)])
}

// Auto-install on first import so we never miss anything.
try {
  installGlobalHooks()
} catch (e) {
  /* no-op */
}
